// imports
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

// local imports
import { useUrlParameters } from '../../hooks/useUrlParameters';
import { uiActions } from '../../store';
import {
  setSelectedSector,
  setSelectedModel,
  setSelectedVariable,
  setSelectedStartMonth,
  setSelectedStartYear,
  setSelectedEndMonth,
  setSelectedEndYear,
  setTemporalResolution,
  setAppliedSector,
  setAppliedModel,
  setAppliedVariable,
  setAppliedTemporalResolution,
  setAppliedStartMonth,
  setAppliedStartYear,
  setOpacity,
  initialWaterDataState,
} from '../../store/waterDataSlice';
import { useGetAwdConfigQuery, selectModelsBySector, selectModelById, selectVariablesByModel } from '../../services/awdConfigApi';
import { months, monthKey } from '../../constants/Months';
import { getAvailableTemporalResolutions } from '../../constants/TemporalResolution';

/**
 * Converts a month key (e.g., "05") to a month name (e.g., "May").
 * Returns null if the key is not found.
 */
const monthKeyToName = (key: string | null): string | null => {
  if (!key) return null;
  const month = months.find((m) => m.key === key);
  return month ? month.value : null;
};

/**
 * AWDInitializer
 *
 * Component that initializes the Available Water Data (AWD) Redux state from URL parameters on app load.
 * This ensures URL parameters take precedence over Redux initial state.
 * Validates that the variable is compatible with the selected model.
 */
export const AWDInitializer = () => {
  const dispatch = useDispatch();
  const { getParameter, setParameter } = useUrlParameters();
  const { data: awdConfig } = useGetAwdConfigQuery();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current || !awdConfig) return;
    hasInitialized.current = true;

    // Read all water data URL parameters
    const sectorFromUrl = getParameter('sector');
    const modelFromUrl = getParameter('model');
    const variableFromUrl = getParameter('variable');
    const startMonthFromUrl = getParameter('startMonth');
    const startYearFromUrl = getParameter('startYear');
    const endMonthFromUrl = getParameter('endMonth');
    const endYearFromUrl = getParameter('endYear');
    const temporalResolutionFromUrl = getParameter('temporalResolution');

    // Update Redux state with URL values, falling back to initial state defaults
    const sector = sectorFromUrl ?? initialWaterDataState.selectedSector;

    // Validate model against the sector's available models
    const availableModels = selectModelsBySector(awdConfig.models, sector);
    let model: string;
    if (modelFromUrl && availableModels.some((m) => m.key === modelFromUrl)) {
      model = modelFromUrl;
    } else if (availableModels.some((m) => m.key === initialWaterDataState.selectedModel)) {
      model = initialWaterDataState.selectedModel;
    } else if (availableModels.length > 0) {
      model = availableModels[0].key;
    } else {
      model = initialWaterDataState.selectedModel;
    }

    // Validate variable against the model's available variables
    const availableVariables = selectVariablesByModel(awdConfig.models, model);
    let variable: string;
    if (variableFromUrl && availableVariables.some((v) => v.key === variableFromUrl)) {
      variable = variableFromUrl;
    } else if (availableVariables.some((v) => v.key === initialWaterDataState.selectedVariable)) {
      variable = initialWaterDataState.selectedVariable;
    } else if (availableVariables.length > 0) {
      variable = availableVariables[0].key;
    } else {
      variable = initialWaterDataState.selectedVariable;
    }

    // URL stores months as keys ("05"), but Redux expects month names ("May")
    const modelConfig = selectModelById(awdConfig.models, model);

    // When startYear/startMonth are missing from URL, default to January
    // and the most recent year available for the model
    const startMonth = monthKeyToName(startMonthFromUrl)
      ?? 'January';
    const startYear = startYearFromUrl
      ?? modelConfig?.endYear
      ?? initialWaterDataState.selectedStartYear;

    // Validate startYear/startMonth against the model's date range
    let validatedStartYear = startYear;
    let validatedStartMonth = startMonth;
    if (modelConfig) {
      const modelStartYear = parseInt(modelConfig.startYear);
      const modelEndYear = parseInt(modelConfig.endYear);
      const yearNum = parseInt(validatedStartYear!);
      if (isNaN(yearNum) || yearNum < modelStartYear || yearNum > modelEndYear) {
        validatedStartYear = modelConfig.endYear;
        validatedStartMonth = 'January';
      }
    }

    const endMonth = monthKeyToName(endMonthFromUrl) ?? initialWaterDataState.selectedEndMonth;
    const endYear = endYearFromUrl ?? initialWaterDataState.selectedEndYear;
    const availableResolutions = getAvailableTemporalResolutions();
    const rawTemporalResolution = temporalResolutionFromUrl ?? initialWaterDataState.temporalResolution;
    const temporalResolution = rawTemporalResolution in availableResolutions
      ? rawTemporalResolution
      : initialWaterDataState.temporalResolution;

    const isMonthly = temporalResolution === 'monthly';

    dispatch(setSelectedSector(sector));
    dispatch(setSelectedModel(model));
    dispatch(setSelectedVariable(variable));
    dispatch(setSelectedStartMonth(isMonthly ? validatedStartMonth : null));
    dispatch(setSelectedStartYear(validatedStartYear));
    dispatch(setSelectedEndMonth(endMonth));
    dispatch(setSelectedEndYear(endYear));
    dispatch(setTemporalResolution(temporalResolution));

    // Also initialize the applied* states so the app recognizes these values
    // as already applied (e.g. on refresh or shared link load)
    dispatch(setAppliedSector(sector));
    dispatch(setAppliedModel(model));
    dispatch(setAppliedVariable(variable));
    dispatch(setAppliedStartMonth(isMonthly ? validatedStartMonth : null));
    dispatch(setAppliedStartYear(validatedStartYear));
    dispatch(setAppliedTemporalResolution(temporalResolution));
    dispatch(uiActions.setAwdNoDataProbing(true));

    // Initialize opacity from URL
    const opacityFromUrl = getParameter('opacity');
    const parsedOpacity = opacityFromUrl ? parseInt(opacityFromUrl, 10) : null;
    const opacity = (parsedOpacity !== null && !isNaN(parsedOpacity) && parsedOpacity >= 0 && parsedOpacity <= 100)
      ? parsedOpacity
      : initialWaterDataState.opacity;
    dispatch(setOpacity(opacity));

    // Sync URL to reflect validated/defaulted values
    setParameter('sector', sector);
    setParameter('model', model);
    setParameter('variable', variable);
    setParameter('temporalResolution', temporalResolution);
    setParameter('opacity', String(opacity));
    if (validatedStartYear) setParameter('startYear', validatedStartYear);
    if (isMonthly && validatedStartMonth) setParameter('startMonth', monthKey(validatedStartMonth));
    if (endYear) setParameter('endYear', endYear);
    if (endMonth) setParameter('endMonth', monthKey(endMonth));
  }, [awdConfig, dispatch, getParameter, setParameter]);

  return null;
};

export default AWDInitializer;
